import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Typography,
  Switch,
  Drawer,
  Table,
  Card,
  Tabs,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { taskTemplateService, TaskTemplate, CreateTaskTemplateData } from '../services/taskTemplates';
import { taskTypeService, TaskType, CreateTaskTypeData } from '../services/taskTypes';
import { subTaskTemplateService, CreateSubTaskTemplateData } from '../services/subTaskTemplates';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const { Title } = Typography;
const { TabPane } = Tabs;

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

// Type for managing sub task templates in the form (includes optional id for existing items)
interface SubTaskTemplateFormData extends CreateSubTaskTemplateData {
  id?: number;
  list_type?: 'general' | 'check_in' | 'check_out' | string;
}

export const TaskTemplateFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'task_template.update');
  const canDelete = hasPermission(user, 'task_template.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [isEditing, setIsEditing] = useState(!isEdit);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [subTaskTemplates, setSubTaskTemplates] = useState<SubTaskTemplateFormData[]>([]);
  const [checkInTemplates, setCheckInTemplates] = useState<SubTaskTemplateFormData[]>([]);
  const [checkOutTemplates, setCheckOutTemplates] = useState<SubTaskTemplateFormData[]>([]);
  const [taskTypeDrawerVisible, setTaskTypeDrawerVisible] = useState(false);
  const [taskTypeForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const navigate = useNavigate();
  const isReadOnly = isEdit && !isEditing;

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Task Templates
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  useEffect(() => {
    fetchTaskTypes();
    if (isEdit && id) {
      loadTaskTemplate(parseInt(id, 10));
    }
  }, [isEdit, id]);

  const fetchTaskTypes = async () => {
    try {
      const response = await taskTypeService.getAll();
      setTaskTypes(response.data || []);
    } catch {
      message.error('Failed to load task types');
    }
  };

  const loadTaskTemplate = async (templateId: number) => {
    setLoadingTemplate(true);
    try {
      const template = await taskTemplateService.getById(templateId);
      form.setFieldsValue({
        name: template.name,
        task_type_id: template.task_type_id,
        enabled: template.enabled ?? true,
      });
      // Load sub task templates
      const subTasks = template.subTaskTemplates || (template as any).sub_task_templates || [];
      if (subTasks.length > 0) {
        const normalized = subTasks.map((st: any) => ({
          id: st.id,
          name: st.name,
          enabled: st.enabled ?? true,
          list_type: st.list_type || 'general',
        }));
        setSubTaskTemplates(normalized.filter((st: any) => st.list_type === 'general'));
        setCheckInTemplates(normalized.filter((st: any) => st.list_type === 'check_in'));
        setCheckOutTemplates(normalized.filter((st: any) => st.list_type === 'check_out'));
      } else {
        setSubTaskTemplates([]);
        setCheckInTemplates([]);
        setCheckOutTemplates([]);
      }
    } catch {
      message.error('Failed to load task template');
      navigate('/task-templates');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this task template?')) return;
    
    try {
      await taskTemplateService.delete(parseInt(id, 10));
      message.success('Task template deleted');
      navigate('/task-templates');
    } catch {
      message.error('Failed to delete task template');
    }
  };

  const handleSubmit = async (values: CreateTaskTemplateData) => {
    setSubmitting(true);
    try {
      const payload: CreateTaskTemplateData = {
        ...values,
        enabled: values.enabled ?? true,
      };

      let templateId: number;
      if (isEdit && id) {
        await taskTemplateService.update(parseInt(id, 10), payload);
        templateId = parseInt(id, 10);
        message.success('Task template updated successfully');
      } else {
        const result = await taskTemplateService.create(payload);
        templateId = result.id;
        message.success('Task template created successfully');
      }

      // Save sub task templates
      const allSubTaskTemplates: SubTaskTemplateFormData[] = [
        ...subTaskTemplates.map((st) => ({ ...st, list_type: 'general' })),
        ...checkInTemplates.map((st) => ({ ...st, list_type: 'check_in' })),
        ...checkOutTemplates.map((st) => ({ ...st, list_type: 'check_out' })),
      ];

      // Get existing sub task templates for this template
      const existingSubTasks = isEdit && id 
        ? await subTaskTemplateService.getAll().then(res => 
            (res.data || []).filter((st: any) => st.task_template_id === templateId)
          )
        : [];

      const existingIds = existingSubTasks.map((st: any) => st.id);
      const providedIds = allSubTaskTemplates.filter(st => st.id).map(st => st.id!);
      const idsToDelete = existingIds.filter((existingId: number) => !providedIds.includes(existingId));

      // Delete removed sub tasks
      for (const subTaskId of idsToDelete) {
        await subTaskTemplateService.delete(subTaskId);
      }

      // Create or update sub tasks
      for (const subTask of allSubTaskTemplates) {
        const subTaskPayload = {
          ...subTask,
          task_template_id: templateId,
          enabled: subTask.enabled ?? true,
          list_type: subTask.list_type || 'general',
        };

        if (subTask.id) {
          await subTaskTemplateService.update(subTask.id, subTaskPayload);
        } else {
          await subTaskTemplateService.create(subTaskPayload);
        }
      }

      navigate('/task-templates');
    } catch {
      message.error(isEdit ? 'Failed to update task template' : 'Failed to create task template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTaskType = async (values: CreateTaskTypeData) => {
    try {
      const newTaskType = await taskTypeService.create({
        ...values,
        enabled: values.enabled ?? true,
      });
      setTaskTypes([...taskTypes, newTaskType]);
      form.setFieldsValue({ task_type_id: newTaskType.id });
      setTaskTypeDrawerVisible(false);
      taskTypeForm.resetFields();
      message.success('Task type created successfully');
    } catch {
      message.error('Failed to create task type');
    }
  };

  const addSubTaskTemplate = (listType: 'general' | 'check_in' | 'check_out') => {
    const newItem = { name: '', enabled: true, list_type: listType };
    if (listType === 'check_in') {
      setCheckInTemplates([...checkInTemplates, newItem]);
      return;
    }
    if (listType === 'check_out') {
      setCheckOutTemplates([...checkOutTemplates, newItem]);
      return;
    }
    setSubTaskTemplates([...subTaskTemplates, newItem]);
  };

  const removeSubTaskTemplate = (listType: 'general' | 'check_in' | 'check_out', index: number) => {
    if (listType === 'check_in') {
      setCheckInTemplates(checkInTemplates.filter((_, i) => i !== index));
      return;
    }
    if (listType === 'check_out') {
      setCheckOutTemplates(checkOutTemplates.filter((_, i) => i !== index));
      return;
    }
    setSubTaskTemplates(subTaskTemplates.filter((_, i) => i !== index));
  };

  const updateSubTaskTemplate = (
    listType: 'general' | 'check_in' | 'check_out',
    index: number,
    field: string,
    value: any
  ) => {
    if (listType === 'check_in') {
      const updated = [...checkInTemplates];
      updated[index] = { ...updated[index], [field]: value, list_type: 'check_in' };
      setCheckInTemplates(updated);
      return;
    }
    if (listType === 'check_out') {
      const updated = [...checkOutTemplates];
      updated[index] = { ...updated[index], [field]: value, list_type: 'check_out' };
      setCheckOutTemplates(updated);
      return;
    }
    const updated = [...subTaskTemplates];
    updated[index] = { ...updated[index], [field]: value, list_type: 'general' };
    setSubTaskTemplates(updated);
  };

  const readOnlySubTaskColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (value: string) => value || '-',
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (value: boolean) => (value ? 'Yes' : 'No'),
    },
  ];

  const editableSubTaskColumns = (listType: 'general' | 'check_in' | 'check_out') => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_: any, record: any, index: number) => (
        <Input
          value={record.name}
          onChange={(e) => updateSubTaskTemplate(listType, index, 'name', e.target.value)}
          placeholder="Sub task template name"
        />
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (_: any, record: any, index: number) => (
        <Switch
          checked={record.enabled}
          onChange={(checked) => updateSubTaskTemplate(listType, index, 'enabled', checked)}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: any, __: any, index: number) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeSubTaskTemplate(listType, index)}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {isEdit ? 'Edit Task Template' : 'New Task Template'}
          </Title>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/task-templates')}
            >
              Back
            </Button>
            {isEdit && !isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {isEdit && isEditing && canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingTemplate || (isEdit && !isEditing)}
        initialValues={{
          enabled: true,
        }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Task Template Name */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Task Template Name</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isReadOnly ? (
                        <span>{form.getFieldValue('name') || '-'}</span>
                      ) : (
                        <Form.Item
                          name="name"
                          rules={[{ required: true, message: 'Please enter task template name' }]}
                          style={{ margin: 0 }}
                        >
                          <Input placeholder="Enter task template name" />
                        </Form.Item>
                      )}
                    </td>
                  </tr>
                  {/* Task Type */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Task Type</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isReadOnly ? (
                        <span>
                          {taskTypes.find((type) => type.id === form.getFieldValue('task_type_id'))?.name || '-'}
                        </span>
                      ) : (
                        <Form.Item name="task_type_id" style={{ margin: 0 }}>
                          <Select 
                            placeholder="Select Task Type" 
                            allowClear
                            dropdownRender={(menu) => (
                              <>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                                  <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => setTaskTypeDrawerVisible(true)}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  >
                                    Add Task Type
                                  </Button>
                                </div>
                              </>
                            )}
                          >
                            {taskTypes
                              .filter(type => type.enabled)
                              .map((type) => (
                                <Select.Option key={type.id} value={type.id}>
                                  {type.name}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </td>
                  </tr>
                  {/* Enabled */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Enabled</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isReadOnly ? (
                        <span>{form.getFieldValue('enabled') ? 'Yes' : 'No'}</span>
                      ) : (
                        <Form.Item name="enabled" valuePropName="checked" style={{ margin: 0 }}>
                          <Switch />
                        </Form.Item>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </TabPane>

          {/* Sub Tasks Tab */}
          <TabPane tab="Sub Tasks" key="subtasks">
            <Card bodyStyle={{ padding: '16px' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Check-In List</div>
                  <Table
                    columns={isEditing ? editableSubTaskColumns('check_in') : readOnlySubTaskColumns}
                    dataSource={checkInTemplates.map((st, index) => ({ ...st, key: index }))}
                    pagination={false}
                    size="small"
                  />
                  {isEditing && (
                    <div style={{ marginTop: 8 }}>
                      <Button
                        type="link"
                        style={{ color: '#ff4d4f', padding: 0 }}
                        icon={<PlusOutlined />}
                        onClick={() => addSubTaskTemplate('check_in')}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Check-Out List</div>
                  <Table
                    columns={isEditing ? editableSubTaskColumns('check_out') : readOnlySubTaskColumns}
                    dataSource={checkOutTemplates.map((st, index) => ({ ...st, key: index }))}
                    pagination={false}
                    size="small"
                  />
                  {isEditing && (
                    <div style={{ marginTop: 8 }}>
                      <Button
                        type="link"
                        style={{ color: '#ff4d4f', padding: 0 }}
                        icon={<PlusOutlined />}
                        onClick={() => addSubTaskTemplate('check_out')}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <Table
                columns={isEditing ? editableSubTaskColumns('general') : readOnlySubTaskColumns}
                dataSource={subTaskTemplates.map((st, index) => ({ ...st, key: index }))}
                pagination={false}
                size="small"
              />
              {isEditing && (
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="link"
                    style={{ color: '#ff4d4f', padding: 0 }}
                    icon={<PlusOutlined />}
                    onClick={() => addSubTaskTemplate('general')}
                  >
                    Add
                  </Button>
                </div>
              )}
            </Card>
          </TabPane>
        </Tabs>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          <Space>
            <Button onClick={() => navigate('/task-templates')}>
              Cancel
            </Button>
            {(!isEdit || isEditing) && (
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? 'Save' : 'Create'}
              </Button>
            )}
          </Space>
        </div>
      </Form>

      {/* Task Type Drawer */}
      <Drawer
        title="Add Task Type"
        open={taskTypeDrawerVisible}
        onClose={() => {
          setTaskTypeDrawerVisible(false);
          taskTypeForm.resetFields();
        }}
        width={400}
      >
        <Form
          form={taskTypeForm}
          layout="vertical"
          onFinish={handleCreateTaskType}
        >
          <Form.Item
            name="name"
            label="Task Type Name"
            rules={[{ required: true, message: 'Please enter task type name' }]}
          >
            <Input placeholder="Enter task type name" />
          </Form.Item>

          <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => {
                setTaskTypeDrawerVisible(false);
                taskTypeForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};
